import DashboardCustomizeRoundedIcon from "@mui/icons-material/DashboardCustomizeRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import FolderCopyRoundedIcon from "@mui/icons-material/FolderCopyRounded";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import VpnKeyRoundedIcon from "@mui/icons-material/VpnKeyRounded";
import { Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, UseFormReturn } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  FormContainer,
  FormTabs,
  IPrivilegesDomain,
  useFetch,
} from "@4uhub/lib4uhub";

import { TRolesForm, rolesSchema } from "./RolesSchema";
import RolesPrivileges from "./RolesPrivileges";
import RolesBasicData from "./RolesBasicData";
import RolesService from "../../../../services/roles.service";
import ProfileDashboardPanelsPage from "./ProfileDashboardPanels/ProfileDashboardPanelsPage";
import ProfileReportsPage from "./ProfileReports/ProfileReportsPage";
import ProfileTicketDefaultDocCategoriesPage from "./ProfileTicketDefaultDocCategories/ProfileTicketDefaultDocCategoriesPage";
import ProfileListCategoriesPage from "./ProfileListCategories/ProfileListCategoriesPage";
import ProfileListClassificationsPage from "./ProfileListClassifications/ProfileListClassificationsPage";

const GENERICS_ROUTE =
  process.env.REACT_APP_IDENTIY + "/api/v1/RoleEstablishments/";

const service = new RolesService(GENERICS_ROUTE);

const translationPath = "page.register.roles.";

interface IRolesFormFieldsProps {
  isEditMode: boolean;
  methods: UseFormReturn<TRolesForm>;
  onMethodsReady: (methods: UseFormReturn<TRolesForm>) => void;
}

const RolesFormFields = ({
  isEditMode,
  methods,
  onMethodsReady,
}: IRolesFormFieldsProps) => {
  useEffect(() => {
    onMethodsReady(methods);
  }, [methods, onMethodsReady]);

  return <RolesBasicData showPrivileges={!isEditMode} />;
};

const Roles = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [formMethods, setFormMethods] = useState<UseFormReturn<TRolesForm>>();

  const { sendRequest: save, loading: sLoading } = useFetch(service.create);
  const { sendRequest: update, loading: uLoading } = useFetch(
    service.updateItemById,
  );
  const { sendRequest: get, loading: gLoading } = useFetch(service.getItemById);

  const handleUpdate = (v: TRolesForm, id: string) => {
    return update({
      item: {
        ...v,
        id: id,
        establishmentIds: v.establishments.map((i) => i.id),
        privilegeIds: v.privileges.map((p) => p.id),
        profileIds: v.profiles.map((profile) => profile.id),
      },
      id: id,
    });
  };

  const handleSave = (v: TRolesForm) => {
    return save({
      ...v,
      establishmentIds: v.establishments.map(
        (establishment) => establishment.id,
      ),
      privilegeIds: v.privileges.map((privilege) => privilege.id),
      profileIds: v.profiles.map((profile) => profile.id),
    });
  };

  const loading = sLoading || uLoading || gLoading;
  const isEditMode = !!id;
  const handleMethodsReady = useCallback((methods: UseFormReturn<TRolesForm>) => {
    setFormMethods((currentMethods) =>
      currentMethods?.control === methods.control ? currentMethods : methods,
    );
  }, []);

  const tabs = useMemo(
    () => [
      {
        label: t(translationPath + "privileges_tab"),
        icon: <VpnKeyRoundedIcon fontSize="small" />,
        component: formMethods ? (
          <Controller
            name="privileges"
            control={formMethods.control}
            render={({ field }) => (
              <Grid container spacing={2}>
                <RolesPrivileges
                  value={(field.value ?? []) as IPrivilegesDomain[]}
                  onChange={field.onChange}
                />
              </Grid>
            )}
          />
        ) : (
          <></>
        ),
      },
      {
        label: t(translationPath + "dashboards_tab"),
        icon: <DashboardCustomizeRoundedIcon fontSize="small" />,
        component: <ProfileDashboardPanelsPage />,
      },
      {
        label: t(translationPath + "reports_tab"),
        icon: <DescriptionRoundedIcon fontSize="small" />,
        component: <ProfileReportsPage />,
      },
      {
        label: t(translationPath + "ticket_default_doc_categories_tab"),
        icon: <FolderCopyRoundedIcon fontSize="small" />,
        component: <ProfileTicketDefaultDocCategoriesPage />,
      },
      {
        label: t(translationPath + "list_categories_tab"),
        icon: <CategoryRoundedIcon fontSize="small" />,
        component: <ProfileListCategoriesPage />,
      },
      {
        label: t(translationPath + "list_classifications_tab"),
        icon: <SellRoundedIcon fontSize="small" />,
        component: <ProfileListClassificationsPage />,
      },
    ],
    [formMethods, t],
  );

  return (
    <>
      <FormContainer<TRolesForm>
        loading={loading}
        title={t(translationPath + "roles")}
        defaultValues={{
          privileges: [],
          establishments: [],
          profiles: [],
        }}
        saveFunction={handleSave}
        updateFunction={handleUpdate}
        getFunction={get}
        schema={rolesSchema}
        subtitle={t(translationPath + "new_role")}
        subtitleWatchField={"name"}
        formActionsOnBottom={false}
        marginBottom={5}
        customHeight="fit-content"
        fields={(_, methods) => (
          <RolesFormFields
            isEditMode={isEditMode}
            methods={methods}
            onMethodsReady={handleMethodsReady}
          />
        )}
      />
      {id && (
        <Grid
          container
          spacing={2}
          sx={{ width: "80%", margin: "0 auto", zIndex: 999 }}
        >
          <Grid item sm={12} mb={5}>
            <FormTabs tabs={tabs} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default Roles;
